/**
 * Period selector for filtering analytics by date range.
 * All date boundaries use Bangkok timezone; API receives UTC.
 */

import { haptic } from "../../../../lib/telegram.ts";
import {
  getTodayDateKeyBangkok,
  getWeekStartDateKeyBangkok,
  startOfDayBangkokUTC,
  endOfDayBangkokUTC,
} from "../../../../lib/bangkok-time.ts";

export type Period = "today" | "week" | "month" | "all";

interface PeriodSelectorProps {
  value: Period;
  onChange: (period: Period) => void;
}

const PERIOD_LABELS: Record<Period, string> = {
  today: "Сегодня",
  week: "Неделя",
  month: "Месяц",
  all: "Всё время",
};

export function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  const handleChange = (period: Period) => {
    haptic("light");
    onChange(period);
  };

  return (
    <div className="flex p-1 bg-slate-100 rounded-xl">
      {(Object.keys(PERIOD_LABELS) as Period[]).map((period) => (
        <button
          key={period}
          type="button"
          onClick={() => handleChange(period)}
          className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all ${
            value === period
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500"
          }`}
        >
          {PERIOD_LABELS[period]}
        </button>
      ))}
    </div>
  );
}

/**
 * Get date range for a given period in Bangkok timezone.
 * Returns UTC Date instances for API (API stores and filters in UTC).
 */
export function getPeriodDateRange(period: Period): {
  dateFrom?: Date;
  dateTo?: Date;
} {
  const todayKey = getTodayDateKeyBangkok();

  switch (period) {
    case "today": {
      return {
        dateFrom: startOfDayBangkokUTC(todayKey),
        dateTo: endOfDayBangkokUTC(todayKey),
      };
    }
    case "week": {
      const weekStartKey = getWeekStartDateKeyBangkok();
      return {
        dateFrom: startOfDayBangkokUTC(weekStartKey),
        dateTo: endOfDayBangkokUTC(todayKey),
      };
    }
    case "month": {
      const [y, m] = todayKey.split("-");
      const monthStartKey = `${y}-${m}-01`;
      return {
        dateFrom: startOfDayBangkokUTC(monthStartKey),
        dateTo: endOfDayBangkokUTC(todayKey),
      };
    }
    case "all":
    default:
      return {};
  }
}
